import type { FieldInstanceFields } from "@regrello/graphql-api";
import {
  type RegrelloDataGridV2EditableCellProps,
  useDataGridV2EditableCell,
  useDataGridV2FirstKeystroke,
} from "@regrello/ui-core";
import { toDate } from "date-fns";

import { RegrelloFormFieldDate } from "../../../../formFields/RegrelloFormFieldDate";
import type { DateFieldPluginFrontendValue } from "./types";

export interface RegrelloEditableCellDateProps {
  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /**
   * The current value to display in the cell. This is the value before editing and the value that
   * will be reinstated if editing is canceled.
   */
  value: DateFieldPluginFrontendValue;

  /** The callback to invoke when the user changes the value of the cell. */
  onValueChange: (value: DateFieldPluginFrontendValue) => void;

  /**
   * Whether the date field is a date-only field. If true, the time and timezone portion of the
   * value will be ignored.
   */
  isDateOnlyFieldEnabled: boolean;
}

/** An editable cell in which the user can input a date. Intended for use in `RegrelloDataGridV2`. */
export function RegrelloEditableCellDate({
  api,
  eventKey,
  fieldInstance,
  value,
  onValueChange,
  isDateOnlyFieldEnabled,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellDateProps>) {
  if (fieldInstance.spectrumFieldVersion == null) {
    throw new Error("spectrumFieldVersioIn is unexpectedly not defined");
  }

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({
    api,
    eventKey,
    // These keys are used to cycle through dates/months/years:
    suppressKeyArrowDown: true,
    suppressKeyArrowUp: true,
    suppressKeyArrowLeft: true,
    suppressKeyArrowRight: true,
  });

  const { inputRef } = useDataGridV2FirstKeystroke({
    eventKey,
    onValueChange,
    // It's too difficult to determine which part of the date comes first, and we don't want to get
    // too clever, so just default to null after focusing the input.
    parseEventKey: () => null,
    value,
  });

  return (
    // Date fields are full of content and are difficult to shrink.
    <div className="min-w-42" style={{ width: columnWidth }}>
      <RegrelloFormFieldDate
        autoFocus={true}
        fullWidth={true}
        ignoreTimezone={isDateOnlyFieldEnabled}
        inputClassName="rounded-none"
        inputRef={inputRef}
        isDefaultMarginsOmitted={true}
        // (clewis): We don't stop editing on blur because there are too many interactions that
        // would get in the way (arrow key shortcuts, calendar popover, etc.).
        onChange={(date, _value) => {
          onValueChange(date != null ? toDate(date) : null);
        }}
        onKeyDownCapture={onKeyDownCapture}
        size="x-small"
        value={value}
      />
    </div>
  );
}
